import { EMPTY_STRING, PAYRILLIUM_CONSTANTS } from "../../constants";
import {
  authorizePayment,
  initializeTerminalConfig,
  logPayrilliumAction,
  payrilliumAbortTransaction,
  payrilliumCapturePayment,
  refundPayment,
  reverseAuthorization,
  showBasket,
} from "../../services/payrillium.service";

// --------------- Transaction Flows ---------------

export const payrilliumProcessSale = async ({
  payment_id,
  amount,
  items = [],
  shouldShowBasket = true,
  cardType = PAYRILLIUM_CONSTANTS.CARD_TYPE.CREDIT,
  tip = PAYRILLIUM_CONSTANTS.TIP.DEFAULT,
  paymentTimeOut = PAYRILLIUM_CONSTANTS.TIMEOUT,
  currentStore = {},
  paymentDevice,
}) => {
  logPayrilliumAction(currentStore, "SALE_INITIATED", {
    payment_id,
    amount,
    cardType,
  });

  await initializeTerminalConfig(currentStore, paymentDevice);

  if (shouldShowBasket && items.length > 0) {
    await showBasket(items, amount).catch(() => {});
  }

  const authResponse = await authorizePayment({
    payment_id,
    amount,
    cardType,
    tip,
    paymentTimeOut,
    signature: EMPTY_STRING,
  });

  if (!authResponse?.success) {
    const errorMsg =
      authResponse?.message ||
      (typeof authResponse?.data?.message === "string"
        ? authResponse.data.message
        : null) ||
      "Payment authorization failed";
    logPayrilliumAction(currentStore, "AUTH_FAILED", {
      payment_id,
      amount,
      error: errorMsg,
    });
    throw new Error(errorMsg);
  }

  const authData = authResponse?.data?.message;
  const authTransactionId = authData?.transactionId;

  logPayrilliumAction(currentStore, "AUTH_SUCCESS", {
    payment_id,
    amount,
    authTransactionId,
    cardVendor: authData?.cardVendor,
    maskedPan: authData?.cardNumber,
    approvalCode: authData?.approvalCode,
    entryMode: authData?.entryMode,
  });

  if (!authTransactionId) {
    logPayrilliumAction(currentStore, "AUTH_MISSING_TRANSACTION_ID", {
      payment_id,
      amount,
    });
    throw new Error("Authorization succeeded but no transactionId returned");
  }

  let captureResponse;
  try {
    captureResponse = await payrilliumCapturePayment({
      payment_id,
      transactionId: authTransactionId,
      amount,
      href: authResponse.data?._links?.capture?.href || EMPTY_STRING,
    });
  } catch (captureError) {
    logPayrilliumAction(currentStore, "CAPTURE_ERROR", {
      payment_id,
      amount,
      authTransactionId,
      error: captureError?.message,
    });
    await payrilliumAbortTransaction().catch(() => {});
    logPayrilliumAction(currentStore, "TRANSACTION_ABORTED", {
      payment_id,
      reason: "CAPTURE_ERROR",
    });
    await reverseAuthorization({
      payment_id,
      transaction_id: authTransactionId,
      totalAmount: amount,
    }).catch(() => {});
    logPayrilliumAction(currentStore, "AUTH_REVERSED", {
      payment_id,
      authTransactionId,
      reason: "CAPTURE_ERROR",
    });
    throw new Error(captureError?.message || "Payment capture failed");
  }

  // payrilliumCapturePayment returns a raw axios response; unwrap it
  const captureBody = captureResponse?.data;
  const captureSuccess =
    captureBody?.success ??
    (captureResponse?.status >= 200 && captureResponse?.status < 300);

  if (!captureSuccess) {
    logPayrilliumAction(currentStore, "CAPTURE_FAILED", {
      payment_id,
      amount,
      authTransactionId,
      status: captureResponse?.status,
    });
    await payrilliumAbortTransaction().catch(() => {});
    logPayrilliumAction(currentStore, "TRANSACTION_ABORTED", {
      payment_id,
      reason: "CAPTURE_FAILED",
    });
    await reverseAuthorization({
      payment_id,
      transaction_id: authTransactionId,
      totalAmount: amount,
    }).catch(() => {});
    logPayrilliumAction(currentStore, "AUTH_REVERSED", {
      payment_id,
      authTransactionId,
      reason: "CAPTURE_FAILED",
    });
    throw new Error("Payment capture failed");
  }

  // Support both wrapped { success, data: { message } } and direct response shapes
  const captureData =
    captureBody?.data?.message || captureBody?.message || captureBody;

  logPayrilliumAction(currentStore, "CAPTURE_SUCCESS", {
    payment_id,
    amount,
    authTransactionId,
    captureTransactionId: captureData?.id,
  });

  return {
    creditCardData: {
      paymentBrand: authData?.cardVendor || "Credit",
      maskedPan: authData?.cardNumber || "****",
      paymentTokenValue: captureData?.id,
      approvalCode: authData?.approvalCode || "",
      entryMode: authData?.entryMode || "",
    },
    authTransactionId,
    captureTransactionId: captureData?.id,
  };
};

export const payrilliumProcessRefund = async ({
  payment_id,
  transaction_id,
  amount,
  currentStore = {},
}) => {
  logPayrilliumAction(currentStore, "REFUND_INITIATED", {
    payment_id,
    transaction_id,
    amount,
  });

  await initializeTerminalConfig(currentStore);

  try {
    const result = await refundPayment({ payment_id, transaction_id, amount });
    logPayrilliumAction(currentStore, "REFUND_SUCCESS", {
      payment_id,
      transaction_id,
      amount,
    });
    return result;
  } catch (error) {
    logPayrilliumAction(currentStore, "REFUND_FAILED", {
      payment_id,
      transaction_id,
      amount,
      error: error?.message,
    });
    throw error;
  }
};
